import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as studentService from '../services/studentService';
import type { 
  CreateStudentRequest, 
  UpdateStudentRequest, 
  ListQueryParams,
  ActivityQueryParams 
} from '../types/api';

// Students list query
export const useStudents = (params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.students.list(params),
    queryFn: () => studentService.getStudents(params), // Returns { students: [...], pagination: {...} }
    select: (data) => {
      // API returns { students: Student[], pagination: Pagination }
      // Return consistent structure with students array
      return {
        students: data.students || [],
        pagination: data.pagination || null
      };
    },
  });
};

// Single student query
export const useStudent = (id: string, view?: string) => {
  return useQuery({
    queryKey: queryKeys.students.detail(id),
    queryFn: () => studentService.getStudent(id, view),
    enabled: !!id,
  });
};

// Student activities query
export const useStudentActivities = (id: string, params?: ActivityQueryParams) => {
  return useQuery({
    queryKey: queryKeys.students.activities(id),
    queryFn: () => studentService.getStudentActivities(id, params),
    select: (data) => data.activities || [],
    enabled: !!id,
  });
};

// Student daily report query
export const useStudentDailyReport = (id: string, date?: string) => {
  return useQuery({
    queryKey: queryKeys.students.dailyReport(id, date),
    queryFn: () => studentService.getStudentDailyReport(id, date),
    select: (data) => data,
    enabled: !!id,
  });
};

// Student attendance query
export const useStudentAttendance = (id: string, params?: { start_date?: string; end_date?: string }) => {
  return useQuery({
    queryKey: queryKeys.students.attendance(id),
    queryFn: () => studentService.getStudentAttendance(id, params),
    select: (data) => data,
    enabled: !!id,
  });
};

// Face recognition status query
export const useFaceRecognitionStatus = (id: string) => {
  return useQuery({
    queryKey: queryKeys.students.faceRecognition(id),
    queryFn: () => studentService.getFaceRecognitionStatus(id),
    select: (data) => data,
    enabled: !!id,
  });
};

// Create student mutation
export const useCreateStudent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateStudentRequest) => studentService.createStudent(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.students.lists() });
      if (response && response.school_id) {
        queryClient.invalidateQueries({ 
          queryKey: queryKeys.schools.students(response.school_id) 
        });
      }
      notifications.show({
        title: 'Success',
        message: 'Student created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create student',
        color: 'red',
      });
    },
  });
};

// Update student mutation
export const useUpdateStudent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStudentRequest }) => 
      studentService.updateStudent(id, data),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.students.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(variables.id) });
      if (response && response.school_id) {
        queryClient.invalidateQueries({ 
          queryKey: queryKeys.schools.students(response.school_id) 
        });
      }
      notifications.show({
        title: 'Success',
        message: 'Student updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update student',
        color: 'red',
      });
    },
  });
};

// Delete student mutation
export const useDeleteStudent = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => studentService.deleteStudent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.students.lists() });
      notifications.show({
        title: 'Success',
        message: 'Student deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete student',
        color: 'red',
      });
    },
  });
};

// Upload student photos mutation
export const useUploadStudentPhotos = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, photos }: { id: string; photos: File[] }) => 
      studentService.uploadStudentPhotos(id, photos),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.students.faceRecognition(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Photos uploaded successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to upload photos',
        color: 'red',
      });
    },
  });
};

// Train face recognition mutation
export const useTrainFaceRecognition = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => studentService.trainFaceRecognition(id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.students.faceRecognition(variables) });
      notifications.show({
        title: 'Success',
        message: 'Face recognition training started',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to start face recognition training',
        color: 'red',
      });
    },
  });
};